/**
 * Tab-scoped copilot session id.
 *
 * Persisted in `sessionStorage` so an in-tab panel re-mount or React
 * Strict-Mode double-mount keeps the same id. A new tab gets a fresh
 * id, which matches the server-side semantics: each browser tab is its
 * own copilot session, and parallel tabs are independent.
 */

const STORAGE_KEY = "noki-copilot-session-id";

const generate = () => {
  try {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch {
    /* fall through to the timestamp form */
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export function ensureCopilotSessionId() {
  if (typeof window === "undefined") return generate();
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = generate();
    window.sessionStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // Storage unavailable (private mode, edge cases) — fall back to a
    // run-of-the-tab id. Re-mounts will get a new value, which is the
    // best we can do without storage.
    return generate();
  }
}

/**
 * Wipe the stored id so the next `ensureCopilotSessionId()` mints a
 * fresh one. Called when the server tells us the current id is dead
 * (e.g. server error code `session_closed` or TTL expiry).
 */
export function clearCopilotSessionId() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — nothing to do */
  }
}
